import { makeAutoObservable, runInAction } from 'mobx';
import { EpicType, EpicTypeModel } from '../models/EpicType';
import { addEpicType, deleteEpicType, getEpicTypes, updateEpicType } from '../services/EpicTypesAPI';
import { errorStore } from './ErrorStore';

class EpicTypeStore {
	data: EpicType[] = [];
	isLoading: boolean = false;
	showModal = false;
	currentRow: EpicTypeModel | undefined;

	constructor() {
		makeAutoObservable(this);
	}

	async loadData() {
		if (this.data.length || this.isLoading) return;

		try {
			this.setLoading(true);
			const response = await getEpicTypes();
			this.setData(response || []);
		} catch (error: any) {
			this.setError(error.message);
		} finally {
			this.setLoading(false);
		}
	}

	async delete(epicType: EpicType) {
		try {
			this.setLoading(true);
			await deleteEpicType(epicType.id);
			this.setData(this.data.filter(v => v.id !== epicType.id));
			this.clearForm();
		} catch (error: any) {
			this.setError(error.message);
		} finally {
			this.setLoading(false);
		}
	}

	async update(epicType: EpicType) {
		try {
			this.setLoading(true);
			await updateEpicType(epicType);

			runInAction(() => {
				this.data = this.data.map(et =>
					et.id === epicType.id ? { ...epicType } : et
				);
			});

			this.removeCurrent();
		} catch (error: any) {
			this.setError(error.message);
			throw error;
		} finally {
			this.setLoading(false);
		}
	}

	setupNew() {
		this.currentRow = new EpicTypeModel({ id: 0, name: '', isActive: true });
		this.setShowModal(true);
	}

	async addNew(epicType: EpicType, onSuccess?: () => void) {
		this.setLoading(true);
		try {
			const addedNewEpicType = await addEpicType(epicType);
			if (addedNewEpicType) {
				this.addRow(addedNewEpicType);
				this.clearForm();
				onSuccess?.();
			}
		} catch (error: any) {
			this.setError(error.message)
		} finally {
			this.setLoading(false);
		}
	}

	setLoading(loading: boolean) {
		this.isLoading = loading;
	}

	addRow(epicType: EpicType) {
		this.data = [epicType, ...this.data];
	}

	setError(error: string) {
		errorStore.report(error);
	}

	setShowModal(state: boolean) {
		this.showModal = state;
	}

	removeCurrent() {
		this.currentRow = undefined;
	}

	clearForm() {
		this.removeCurrent();
		this.setShowModal(false);
	}

	setData(epicTypes: EpicType[]) {
		this.data = epicTypes;
	}
}

export const epicTypeStore = new EpicTypeStore();

